import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAsync — small wrapper around `useState` for "load-then-display" patterns.
 *
 * Cancels any in-flight call when the dependency changes; tracks the latest
 * request so a slow earlier response doesn't overwrite a fresher one.
 */

export interface AsyncState<T> {
  data?: T;
  error?: Error;
  loading: boolean;
}

export function useAsync<T>(loader: (signal: AbortSignal) => Promise<T>, deps: unknown[]): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ loading: true });
  const requestId = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoLoader = useCallback(loader, deps);

  const run = useCallback(() => {
    const id = ++requestId.current;
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true }));
    memoLoader(controller.signal)
      .then((data) => {
        if (id !== requestId.current) return;
        setState({ data, loading: false });
      })
      .catch((error: unknown) => {
        if (id !== requestId.current) return;
        if (error instanceof Error && error.name === "AbortError") return;
        setState({ error: error instanceof Error ? error : new Error(String(error)), loading: false });
      });
    return () => controller.abort();
  }, [memoLoader]);

  useEffect(() => {
    const cancel = run();
    return cancel;
  }, [run]);

  return { ...state, reload: run };
}
